import { useEffect, useMemo, useState, useCallback } from 'react';
import { Calendar, Dumbbell, ExternalLink, Utensils, Trophy } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';
import { isLiveClassClosed, isLiveClassEnrollable, isLiveClassInProgress } from '../../utils/liveClassEnrollment';
import { dateKeyInScheduleTz, D28D_SCHEDULE_TZ } from '../../utils/d28dScheduleTimezone';
import { isVirtualGymMember } from './userServices';
import {
  buildEnrollSuccessMessage,
  rememberEnrollBanner,
  resolveClassEndMs,
  syncEnrollBannersFromClasses,
} from '../../utils/liveClassEnrollBanner';
import LiveClassEnrollBanner from './LiveClassEnrollBanner';

function isEnrolled(classItem, userId) {
  const ids = Array.isArray(classItem?.enrolled_user_ids) ? classItem.enrolled_user_ids : [];
  return ids.some((id) => Number(id) === Number(userId));
}

function classDayKey(classItem) {
  return dateKeyInScheduleTz(classItem?.start_time, D28D_SCHEDULE_TZ);
}

/** Hoy si hay clases; si no, el próximo día con clases (todas las de ese día). */
function pickPriorityDayClasses(list) {
  const todayKey = dateKeyInScheduleTz(new Date().toISOString(), D28D_SCHEDULE_TZ);
  const open = (list || [])
    .filter((c) => c.active !== false && !isLiveClassClosed(c))
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  const todayOnes = open.filter((c) => classDayKey(c) === todayKey);
  if (todayOnes.length) {
    return { classes: todayOnes, dayKey: todayKey, isToday: true };
  }
  const future = open.filter((c) => classDayKey(c) >= todayKey);
  if (!future.length) return { classes: [], dayKey: '', isToday: true };
  const dayKey = classDayKey(future[0]);
  return {
    classes: future.filter((c) => classDayKey(c) === dayKey),
    dayKey,
    isToday: false,
  };
}

function formatClassTime(iso, lang) {
  try {
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'es-CO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: D28D_SCHEDULE_TZ,
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

function formatDayLabel(dayKey, lang) {
  if (!dayKey) return '';
  try {
    const noon = new Date(`${dayKey}T12:00:00`);
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'es-CO', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      timeZone: D28D_SCHEDULE_TZ,
    }).format(noon);
  } catch {
    return dayKey;
  }
}

/**
 * Un solo bloque hero según prioridad: clase > entrenamiento > food > premiación.
 */
export default function UserDayHero({
  hasD28d,
  hasTraining,
  hasFood,
  coachFoodUpsell = false,
  sedeFoodUpsell = false,
  hideD28dBranding,
  sedeFinalMode = false,
  programId,
  onOpenClasses,
  onOpenTraining,
  onOpenGallery,
  onOpenFood,
  onAcquireFood,
  onOpenProgress,
}) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const virtualGymMember = isVirtualGymMember(user);
  const [loading, setLoading] = useState(true);
  const [priorityDay, setPriorityDay] = useState({ classes: [], dayKey: '', isToday: true });
  const [enrollingId, setEnrollingId] = useState(null);
  const [training, setTraining] = useState(null);
  const [coachPlan, setCoachPlan] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [cycleCtx, setCycleCtx] = useState(null);
  const [challengeEnrolling, setChallengeEnrolling] = useState(false);
  const enrollInActiveChallenge = useCallback(async (challengeItem) => {
    if (!challengeItem?.id || challengeItem.user_enrolled) return;
    setChallengeEnrolling(true);
    try {
      await api.post(`/d28d/challenges/${challengeItem.id}/enroll`);
      setChallenge((prev) => (prev ? { ...prev, user_enrolled: true } : prev));
    } catch {
      /* navegación igual aunque falle inscripción */
    } finally {
      setChallengeEnrolling(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const tasks = [];
      const showLive = (hasD28d && !hideD28dBranding) || sedeFinalMode;
      // Sede final: no pedir / mostrar ciclo D28D (acceso = vigencia).
      const showCycle = hasD28d && !hideD28dBranding && !sedeFinalMode;
      if (showLive) {
        tasks.push(
          api.get('/live-classes', { params: programId ? { program_id: programId } : {} })
            .then((r) => pickPriorityDayClasses(r.data?.data || []))
            .catch(() => ({ classes: [], dayKey: '', isToday: true })),
        );
      } else tasks.push(Promise.resolve({ classes: [], dayKey: '', isToday: true }));

      if (showCycle) {
        tasks.push(
          api.get('/d28d/cycle/me').then((r) => r.data?.data || null).catch(() => null),
        );
      } else tasks.push(Promise.resolve(null));

      if (hasTraining) {
        tasks.push(
          api.get('/training/progress/me').then((r) => r.data?.data || null).catch(() => null),
        );
        tasks.push(
          api.get('/training/my-current-plan').then((r) => r.data?.plan || null).catch(() => null),
        );
      } else {
        tasks.push(Promise.resolve(null));
        tasks.push(Promise.resolve(null));
      }

      if (hasD28d && !hideD28dBranding && !sedeFinalMode) {
        tasks.push(
          api.get('/d28d/challenges', { params: programId ? { program_id: programId } : {} })
            .then((r) => (r.data?.data || []).find((c) => c.estado === 'active') || null)
            .catch(() => null),
        );
      } else tasks.push(Promise.resolve(null));

      const [dayBundle, cycleData, trainingProgress, assignedPlan, challengeData] = await Promise.all(tasks);
      if (!active) return;
      setCycleCtx(showCycle ? cycleData : null);
      setPriorityDay(dayBundle && dayBundle.classes ? dayBundle : { classes: [], dayKey: '', isToday: true });
      if (user?.id && dayBundle?.classes?.length) {
        syncEnrollBannersFromClasses(user.id, dayBundle.classes);
      }
      setTraining(trainingProgress);
      setCoachPlan(assignedPlan);
      setChallenge(challengeData);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [hasD28d, hasTraining, hideD28dBranding, sedeFinalMode, programId, user?.id, virtualGymMember]);

  const refreshPriorityDay = useCallback(async () => {
    try {
      const res = await api.get('/live-classes', { params: programId ? { program_id: programId } : {} });
      const dayBundle = pickPriorityDayClasses(res.data?.data || []);
      setPriorityDay(dayBundle);
      if (user?.id) syncEnrollBannersFromClasses(user.id, dayBundle.classes || []);
    } catch {
      setPriorityDay({ classes: [], dayKey: '', isToday: true });
    }
  }, [programId, user?.id]);

  const handleEnroll = useCallback(async (classId) => {
    const id = Number(classId);
    if (!Number.isFinite(id)) return;
    const classItem = priorityDay.classes.find((c) => Number(c.id) === id);
    if (!classItem || !isLiveClassEnrollable(classItem)) {
      emitToast({
        type: 'warning',
        title: t('schedule.enroll_fail', 'Inscripción'),
        message: t('schedule.enrollment_closed', 'Esta clase ya terminó. No hay inscripciones abiertas.'),
      });
      return;
    }
    if (isEnrolled(classItem, user?.id)) return;
    setEnrollingId(id);
    try {
      const res = await api.post(`/live-classes/${id}/enroll`);
      const updated = res.data?.data;
      const title = updated?.title || classItem.title;
      const endMs = resolveClassEndMs(updated || classItem);
      if (updated?.enrolled_user_ids) {
        setPriorityDay((prev) => ({
          ...prev,
          classes: (prev.classes || []).map((c) => (
            Number(c.id) === id
              ? { ...c, enrolled_user_ids: updated.enrolled_user_ids, zoom_link: updated.zoom_link || c.zoom_link }
              : c
          )),
        }));
      }
      rememberEnrollBanner({
        userId: user?.id,
        classId: id,
        classTitle: title,
        endMs,
      });
      emitToast({
        type: 'success',
        title: t('schedule.enroll_ok_title', 'Inscripción confirmada'),
        message: buildEnrollSuccessMessage(title),
        duration: 12000,
      });
      try {
        window.dispatchEvent(new CustomEvent('d28d:progress:refresh'));
        window.dispatchEvent(new CustomEvent('d28d:notifications:refresh'));
      } catch { /* noop */ }
      await refreshPriorityDay();
      // No abrir Zoom aquí: igual que Clases en vivo — el usuario pulsa el botón ZOOM.
    } catch (err) {
      emitToast({
        type: 'error',
        title: t('schedule.enroll_fail', 'Inscripción'),
        message: err.response?.data?.error || t('common.error', 'Error'),
      });
    } finally {
      setEnrollingId(null);
    }
  }, [priorityDay.classes, refreshPriorityDay, t, user?.id]);

  const handleJoin = useCallback(async (classItem) => {
    const id = Number(classItem?.id);
    let link = String(classItem?.zoom_link || '').trim();
    if (!link) {
      emitToast({
        type: 'info',
        title: t('schedule.no_zoom', 'Sin enlace Zoom'),
        message: t('schedule.zoom_pending', 'Enlace pendiente: tu entrenador lo publicará antes de la clase.'),
      });
      return;
    }
    try {
      const res = await api.post(`/live-classes/${id}/join`);
      const fromJoin = String(res.data?.data?.zoom_link || '').trim();
      if (fromJoin) link = fromJoin;
      try {
        window.dispatchEvent(new CustomEvent('d28d:progress:refresh'));
      } catch { /* noop */ }
    } catch (err) {
      const msg = err.response?.data?.error;
      if (msg) emitToast({ type: 'warning', title: t('schedule.attendance', 'Asistencia'), message: msg });
    }
    const opened = window.open(link, '_blank', 'noopener,noreferrer');
    if (!opened) {
      window.location.assign(link);
    }
  }, [t]);

  const hero = useMemo(() => {
    if (((hasD28d && !hideD28dBranding) || sedeFinalMode) && priorityDay.classes.length > 0) {
      const dayLabel = priorityDay.isToday
        ? t('home.hero.today_classes', 'Clases de hoy')
        : t('home.hero.day_classes', 'Clases del {day}', { day: formatDayLabel(priorityDay.dayKey, lang) });
      return {
        kind: 'class',
        eyebrow: dayLabel,
        classes: priorityDay.classes,
        secondary: t('home.view_calendar', 'Ver calendario'),
        onSecondary: () => onOpenClasses?.(),
        Icon: Calendar,
      };
    }
    if (hasD28d && !hideD28dBranding && !sedeFinalMode && cycleCtx?.cycle_day) {
      return {
        kind: 'cycle',
        eyebrow: t('d28d.cycle.your_cycle', 'Tu ciclo'),
        title: cycleCtx.cycle?.name || t('d28d.cycle.current', 'Ciclo activo'),
        meta: t('d28d.cycle.day_of', 'Día {day} de {total}', {
          day: cycleCtx.cycle_day,
          total: cycleCtx.cycle_total_days || 28,
        }),
        cta: t('home.view_progress', 'Ver progreso'),
        secondary: t('home.live_classes_short', 'Clases en vivo'),
        onPrimary: () => onOpenProgress?.('general'),
        onSecondary: () => onOpenClasses?.(),
        Icon: Calendar,
      };
    }
    if (hasTraining && coachPlan?.dias?.length) {
      const firstDay = coachPlan.dias[0];
      return {
        kind: 'training-plan',
        eyebrow: t('home.hero.training', 'Entrenamiento'),
        title: firstDay?.nombre || t('home.hero.continue_training', 'Continúa tu rutina'),
        meta: t('home.routine_days_meta', '{days} día(s) asignados por tu coach', { days: coachPlan.dias.length }),
        cta: t('home.hero.start_session', 'Iniciar sesión'),
        secondary: t('home.open_gallery', 'Abrir biblioteca'),
        onPrimary: () => onOpenTraining?.(),
        onSecondary: () => onOpenGallery?.(),
        Icon: Dumbbell,
      };
    }
    if (hasTraining && training && (training.planned > 0 || training.adherence_pct != null)) {
      const pending = Math.max(0, (training.planned || 0) - (training.executed || 0));
      return {
        kind: 'training',
        eyebrow: t('home.hero.training', 'Entrenamiento'),
        title: pending > 0
          ? t('home.hero.continue_training', 'Continúa tu rutina')
          : t('home.hero.training_done', 'Buen trabajo esta semana'),
        meta: t('home.hero.training_meta', '{pct}% cumplimiento · {done}/{plan} sesiones', {
          pct: training.adherence_pct ?? 0,
          done: training.executed ?? 0,
          plan: training.planned ?? 0,
        }),
        cta: t('home.hero.start_session', 'Iniciar sesión'),
        secondary: t('home.view_progress', 'Ver progreso'),
        onPrimary: () => onOpenTraining?.(),
        onSecondary: () => onOpenProgress?.('entrenamiento'),
        Icon: Dumbbell,
      };
    }
    if ((coachFoodUpsell || sedeFoodUpsell) && !sedeFinalMode && !virtualGymMember) {
      return {
        kind: 'food',
        eyebrow: t('home.hero.nutrition', 'Alimentación'),
        title: t('home.hero.food_today', 'Tu plan del día'),
        meta: t('home.hero.food_external_meta', 'Abre FOOD PLAN en el ecosistema para registrar tu alimentación.'),
        cta: t('mi_progreso.open_food', 'Ir a Food Plan'),
        secondary: null,
        onPrimary: () => onOpenFood?.(),
        Icon: Utensils,
      };
    }
    if (hasFood && !sedeFinalMode && !virtualGymMember) {
      return {
        kind: 'food',
        eyebrow: t('home.hero.nutrition', 'Alimentación'),
        title: t('home.hero.food_today', 'Tu plan del día'),
        meta: t('home.hero.food_meta', 'Registra comidas y mantén tu progreso nutricional.'),
        cta: t('home.hero.view_food', 'Ver alimentación'),
        secondary: null,
        onPrimary: () => onOpenFood?.(),
        Icon: Utensils,
      };
    }
    if (hasD28d && !hideD28dBranding && !sedeFinalMode && challenge) {
      const enrolled = challenge.user_enrolled === true;
      return {
        kind: 'challenge',
        eyebrow: t('home.hero.challenge', 'Premiación activa'),
        title: challenge.nombre,
        meta: challenge.objetivo || t('home.hero.challenge_meta', 'Sube tus resultados y compite en el ciclo.'),
        cta: enrolled
          ? t('home.hero.view_challenge', 'Ver premiación')
          : t('home.hero.ascend_challenge', 'Ascender premiación'),
        secondary: null,
        onPrimary: async () => {
          if (!enrolled) await enrollInActiveChallenge(challenge);
          onOpenProgress?.('general');
        },
        enrolling: challengeEnrolling,
        Icon: Trophy,
      };
    }
    if (sedeFinalMode) {
      return {
        kind: 'sede',
        eyebrow: t('home.hero.sede_welcome', 'Tu sede virtual'),
        title: t('home.hero.sede_classes', 'Clases en vivo y grabaciones'),
        meta: t('home.hero.sede_meta', 'Consulta el calendario, inscríbete y revisa tu progreso.'),
        cta: t('home.view_calendar', 'Ver calendario'),
        secondary: null,
        onPrimary: () => onOpenClasses?.(),
        Icon: Calendar,
      };
    }
    return null;
  }, [
    hasD28d, hasTraining, hasFood, coachFoodUpsell, hideD28dBranding, sedeFinalMode, virtualGymMember, priorityDay, training, coachPlan, challenge, cycleCtx,
    user?.id, lang, t, onOpenClasses, onOpenTraining, onOpenGallery, onOpenFood, onAcquireFood, onOpenProgress, challengeEnrolling, enrollInActiveChallenge,
  ]);

  if (loading) {
    return (
      <div className="ef-hero animate-pulse">
        <div className="h-3 bg-white/10 rounded w-1/4 mb-3" />
        <div className="h-7 bg-white/10 rounded w-3/4 mb-2" />
        <div className="h-4 bg-white/10 rounded w-1/2" />
      </div>
    );
  }

  if (!hero) {
    return null;
  }

  const { Icon, eyebrow, title, meta, cta, secondary, onPrimary, onSecondary, classes } = hero;

  return (
    <>
    <div className="ef-hero">
      <LiveClassEnrollBanner compact />
      <div className="flex gap-3 items-start">
        <div className="hidden sm:flex h-11 w-11 rounded-xl bg-[rgba(255,215,0,0.12)] items-center justify-center shrink-0">
          <Icon className="w-6 h-6 text-[var(--d28d-yellow)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="ef-hero__eyebrow mb-0">{eyebrow}</p>
          </div>
          {hero.kind === 'class' && classes?.length ? (
            <div className="ef-hero-class-grid mt-2">
              {classes.map((classItem) => {
                const enrolled = isEnrolled(classItem, user?.id);
                const canEnroll = isLiveClassEnrollable(classItem);
                const inProgress = isLiveClassInProgress(classItem);
                const hasZoom = Boolean(String(classItem.zoom_link || '').trim());
                return (
                  <article key={classItem.id} className="ef-hero-class-chip">
                    <h3 className="ef-hero-class-chip__title" title={classItem.title}>{classItem.title}</h3>
                    <p className="ef-hero-class-chip__meta">
                      {formatClassTime(classItem.start_time, lang)}
                      {classItem.coach ? ` · ${classItem.coach.split(' ')[0]}` : ''}
                    </p>
                    <div className="ef-hero-class-chip__action">
                      {enrolled ? (
                        <>
                          {hasZoom ? (
                            <button type="button" className="btn btn-primary text-xs w-full" onClick={() => handleJoin(classItem)}>
                              <ExternalLink className="w-3 h-3 inline mr-1" />
                              Zoom
                            </button>
                          ) : (
                            <span className="text-[10px] text-amber-300/90 leading-tight">
                              {t('schedule.zoom_pending', 'Enlace pendiente')}
                            </span>
                          )}
                          {!isLiveClassClosed(classItem) ? (
                            <span className="ef-hero-class-chip__enrolled-note">
                              {t('schedule.enroll_chip_note', 'Registrado · conéctate 5 min antes')}
                            </span>
                          ) : null}
                        </>
                      ) : canEnroll && inProgress && hasZoom ? (
                        <button type="button" className="btn btn-primary text-xs w-full" onClick={() => handleJoin(classItem)}>
                          <ExternalLink className="w-3 h-3 inline mr-1" />
                          Zoom
                        </button>
                      ) : canEnroll ? (
                        <button
                          type="button"
                          className="btn btn-primary text-xs w-full"
                          disabled={enrollingId === classItem.id}
                          onClick={() => handleEnroll(classItem.id)}
                        >
                          {enrollingId === classItem.id
                            ? t('common.loading', '…')
                            : t('home.enroll_class', 'Inscribirme')}
                        </button>
                      ) : (
                        <span className="text-[10px] ef-hero__meta leading-tight">
                          {t('schedule.enrollment_closed', 'Cerrada')}
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <>
              <h2 className="ef-hero__title">{title}</h2>
              {meta ? <p className="ef-hero__meta">{meta}</p> : null}
            </>
          )}
          <div className="ef-hero__actions">
            {cta && onPrimary ? (
              <button type="button" className="btn btn-primary" disabled={hero.enrolling} onClick={onPrimary}>{cta}</button>
            ) : null}
            {secondary && onSecondary ? (
              <button type="button" className="btn btn-secondary" onClick={onSecondary}>{secondary}</button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
